<template>
  <d-modal @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        Dodaj produkt
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer
          ref="order-product-form"
          tag="form"
          @keydown.enter.prevent>
          <div class="form-group">
            <label for="product_id">
              Produkt:
            </label>

            <validation-provider
              rules="required"
              name="product-id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  v-model="product_id"
                  :state="errors.length ? 'invalid' : null"
                  :reduce="(product) => product.id"
                  placeholder="Wybierz produkt"
                  label="code"
                  :options="products">
                  <template slot="no-options">
                    Brak takiego produktu&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}<br>
                      <small>(Kod produktu: {{ option.code }})</small>
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}<br>
                      <small>(Kod produktu: {{ option.code }})</small>
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              Liczba sztuk:
            </label>

            <validation-provider
              rules="required|min_value:1"
              name="quantity"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                type="number"
                min="1"
                step="1"
                v-model="quantity" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="product_id"
            class="form-group">
            <label for="size">
              Rozmiar:
            </label>

            <validation-provider
              v-if="availableSizes.length"
              rules="required"
              name="product-size"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  v-model="size_id"
                  :reduce="data => data.id"
                  label="name"
                  placeholder="Wybierz rozmiar"
                  :options="availableSizes" />
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>

            <div v-else>
              <span class="text-danger">
                Ten produkt nie ma zdefiniowanych rozmiarów
              </span>
            </div>
          </div>

          <div
            v-if="product_id"
            class="form-group">
            <label for="material">
              Materiał:
            </label>

            <validation-provider
              v-if="availableSizes.length"
              rules="required"
              name="product-material"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  v-model="material_id"
                  placeholder="Wybierz materiał"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :reduce="option => option.value"
                  label="text"
                  :options="availableProductMaterials" />
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="product_id && material_id"
            class="form-group">
            <label for="material-color">
              Kolor materiału:
            </label>

            <validation-provider
              rules="required"
              name="product-color"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  v-model="color_id"
                  placeholder="Wybierz kolor materiału"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :reduce="option => option.value"
                  label="text"
                  :options="availableMaterialColors" />
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="polar">
              Polar:
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                v-model="polar"
                :reduce="(polar) => polar.id"
                placeholder="Wybierz polar"
                label="name"
                :options="polars" />
            </div>
          </div>

          <div class="form-group">
            <label for="seam">
              Przeszycie
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                v-model="seam"
                :reduce="(seam) => seam.id"
                placeholder="Wybierz przeszycie"
                label="name"
                :options="seams" />
            </div>
          </div>

          <div
            v-if="!productHasDefaultStrand"
            class="form-group">
            <label for="strand">
              Domyślna nić (opcjonalnie)
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                v-model="default_strand"
                :reduce="(strand) => strand.id"
                placeholder="Wybierz nić"
                label="name"
                :options="strands" />
            </div>
          </div>

          <div
            v-if="product_id && size_id"
            class="form-group">
            <label for="procedure_id">
              Procedura:
            </label>

            <div
              v-if="availableProcedures.length"
              :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                v-model="procedure_id"
                placeholder="Wybierz procedurę"
                label="name"
                :reduce="data => data.id"
                :options="availableProcedures" />
            </div>

            <div v-else>
              <span class="text-danger">
                Ten produkt nie ma zdefiniowanych procedur
              </span>
            </div>
          </div>

          <div v-else-if="product_id">
            <span class="text-danger">
              Aby móc wybrać procedurę, należy najpierw wybrać rozmiar
            </span>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel"
        :disabled="saveInProgress">
        Anuluj
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm"
        :disabled="saveInProgress">
        <div
          v-if="saveInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        <template v-else>
          Dodaj produkt
        </template>
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'order-product-popup',
  props: [
    'products',
    'materials',
    'polars',
    'procedures',
    'seams',
    'sizes',
    'strands'
  ],
  computed: {
    availableSizes () {
      if (!this.product_id) {
        return [];
      }

      if (this.isProductWithAllColorsAndSizes) {
        return this.sizes.map(size => ({
          id: size.id,
          name: size.name
        }));
      }

      let foundedProduct = this.products.filter(product => product.id === this.product_id);

      if (foundedProduct[0]) {
        let sizes = JSON.parse(JSON.stringify(foundedProduct[0].sizes));

        if (sizes.length) {
          sizes = sizes.map(size => ({
            id: size.id,
            name: size.name
          }));
        }

        return sizes;
      }

      return [];
    },
    availableProcedures () {
      if (!this.product_id) {
        return [];
      }

      let foundedProduct = this.products.filter(product => product.id === this.product_id);

      if (foundedProduct[0]) {
        let procedures = JSON.parse(JSON.stringify(foundedProduct[0].procedures));

        if (procedures.length) {
          procedures = procedures.map(procedure => ({
            id: procedure.id,
            name: procedure.name
          }));
        }

        return procedures;
      }

      return [];
    },
    availableProductMaterials () {
      return this.materials.map(color => ({
        text: color.name,
        value: color.id
      }));
    },
    hasDefinedMaterialID () {
      if (!this.product_id) {
        return false;
      }

      let foundedProduct = this.products.filter(product => product.id === this.product_id);

      if (foundedProduct[0]) {
        return foundedProduct[0].material_id;
      }

      return false;
    },
    hasDefinedColorID () {
      if (!this.product_id) {
        return false;
      }

      let foundedProduct = this.products.filter(product => product.id === this.product_id);

      if (foundedProduct[0]) {
        return foundedProduct[0].color_id;
      }

      return false;
    },
    availableMaterialColors () {
      if (this.material_id) {
        let foundedMaterial = this.materials.filter(material => material.id === this.material_id);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => ({
            text: color.name,
            value: color.id
          }));
        }

        return [];
      }

      return [];
    },
    productHasDefaultStrand () {
      if (this.product_id) {
        let foundedProduct = this.products.filter(product => product.id === this.product_id);

        if (foundedProduct[0]) {
          return !!foundedProduct[0].strand_id;
        }
      }

      return false;
    },
    productsWithAllColorsAndSizes () {
      return this.products.filter(product => product.price_code === product.code).map(product => product.id);
    },
    isProductWithAllColorsAndSizes () {
      return this.productsWithAllColorsAndSizes.indexOf(this.product_id) > -1;
    }
  },
  data () {
    return {
      product_id: null,
      material_id: null,
      color_id: null,
      quantity: 1,
      size_id: null,
      polar: null,
      seam: null,
      default_strand: null,
      procedure_id: null,
      saveInProgress: false
    };
  },
  watch: {
    product_id (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.size_id = null;
        this.polar = null;
        this.seam = null;
        this.procedure_id = null;
        this.material_id = null;
        this.color_id = null;
        this.default_strand = null;

        Vue.nextTick(() => {
          if (this.availableSizes.length === 1) {
            this.size_id = this.availableSizes[0].id;

            if (this.availableProcedures.length === 1) {
              this.procedure_id = this.availableProcedures[0].id;
            }
          }

          if (this.hasDefinedMaterialID) {
            this.material_id = this.hasDefinedMaterialID;
          }

          Vue.nextTick(() => {
            if (this.hasDefinedColorID) {
              this.color_id = this.hasDefinedColorID;
            }
          });
        });
      }
    },
    size_id (newValue, oldValue) {
      if (newValue !== oldValue) {
        if (this.availableProcedures.length === 1) {
          this.procedure_id = this.availableProcedures[0].id;
        }
      }

      if (newValue === null) {
        this.procedure_id = null;
      }
    },
    material_id (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.color_id = null;
      }
    }
  },
  methods: {
    handleClose () {
      this.$bus.$emit('order-product-popup-close');
    },
    validateForm () {
      FormUtils.validate(this.$refs['order-product-form'], this.save);
    },
    save () {
      if (this.product_id && this.material_id && this.color_id) {
        this.saveInProgress = true;

        this.$http.post('/materials-stock/products/material-usage', {
          material_id: this.material_id,
          product_id: this.product_id,
          size_id: this.size_id
        }).then((response) => {
          this.saveInProgress = false;

          if (response.data.success) {
            this.submitData(response.data.usage);
          } else {
            this.submitData(0);
          }
        }).catch(() => {
          this.saveInProgress = false;
          this.cancel();

          Vue.swal({
            title: 'Dodawanie nie powiodło się',
            text: 'Nie powiodło się wczytanie danych o użyciu materiałów w produkcie. Spróbuj ponownie...',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        });
      } else {
        this.saveInProgress = false;
        this.submitData(null);
      }
    },
    submitData (usagePerCm) {
      let dataToAdd = {
        product_id: this.product_id,
        quantity: this.quantity,
        size: this.size_id,
        polar: this.polar,
        seam: this.seam,
        procedure_id: this.procedure_id,
        default_strand: this.default_strand,
        usage_cm_per_item: usagePerCm
      };

      if (this.material_id !== null && this.color_id !== null) {
        dataToAdd.color_id = this.color_id;
      }

      this.$bus.$emit('order-product-add', dataToAdd);
      this.handleClose();
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>
